import * as _ from 'lodash'
import config from 'config'
import moment from 'moment'
import orgamax from 'ampersand-app'
import q from 'q'
import AmpersandModel from 'ampersand-model'

export default AmpersandModel.extend({
  constructor: function BaseModel() {
    AmpersandModel.apply(this, arguments)
  },

  // define additional data types
  dataTypes: {
    date: {
      set: function(newVal) {
        if (!newVal || newVal === config.dateEpochValue) {
          return { val: null, type: 'date' }
        }

        const date = moment(newVal, [
          config.dateFormat.api,
          config.dateFormat.client,
        ])

        return {
          val: date.format(config.dateFormat.api),
          type: date.isValid() ? 'date' : null,
        }
      },

      get: function(val) {
        return !val ? null : moment(val).format(config.dateFormat.client)
      },
    },
    datetime: {
      set: function(newVal) {
        if (!newVal || newVal === config.datetimeEpochValue) {
          return { val: null, type: 'datetime' }
        }

        const date = moment(newVal, config.datetimeFormat.api)

        return {
          val: date.format(config.datetimeFormat.api),
          type: date.isValid() ? 'datetime' : null,
        }
      },

      get: function(val) {
        return !val ? null : moment(val).format(config.datetimeFormat.api)
      },
    },
  },

  // send auth header with every request
  ajaxConfig: function() {
    return orgamax.user.token ? { headers: { authorization: `Bearer ${orgamax.user.token}` } } : {}
  },

  destroy: function(options) {
    options = options || {}

    const deferred = q.defer()
    options.success = deferred.resolve
    options.error = function(model, request) {
      deferred.reject(request)
    }

    AmpersandModel.prototype.destroy.call(this, options)
    return deferred.promise
  },

  fetch: function(options) {
    options = options || {}

    const deferred = q.defer()
    options.success = (model, response, options) => {
      if (!response.data) {
        deferred.resolve()
        return
      }
      if (!model.set(model.parse(response.data, options), options)) return false
      deferred.resolve(model)
    }

    options.error = function(collection, response, options) {
      // test for json parse errors and explicitely point them out
      // because ampersand-sync and the underlying xhr module don't
      try {
        JSON.parse(response.body)
        deferred.reject(new Error('fetch error'))
      } catch (err) {
        err.message = `JSON.parse error: ${err.message}`
        deferred.reject(err)
      }
    }

    AmpersandModel.prototype.fetch.call(this, options)
    return deferred.promise
  },

  save: function(key, val, options) {
    const valOptions = key == null || typeof key === 'object'

    options = (valOptions ? val : options) || {}

    const deferred = q.defer()
    options.success = deferred.resolve
    options.error = function(model, request) {
      deferred.reject(request.body)
    }

    AmpersandModel.prototype.save.apply(this, valOptions ? [ key, options ] : [ key, val, options ])
    return deferred.promise
  },

  // logout on expired token
  initialize: function() {
    this.on('error', function(collection, xhr, options) {
      if (xhr.status === 401) {
        orgamax.user.logout()
      }
    })
  },

  // handle xdata url syntax
  url: function() {
    const url = AmpersandModel.prototype.url.call(this)
    return url
  },

  sync: function(method, model, options) {
    options.timeout = options.timeout || config.requestTimeout
    const xhr = AmpersandModel.prototype.sync.call(this, method, model, options)

    // concurrent requests are not aborted
    if (options.isConcurrent) {
      xhr.isConcurrent = true
    }

    orgamax.registry.store(xhr)
    return xhr
  },

  // Mapping
  // =======
  //
  // Models can define a mapping object that maps incoming attribute names to the models
  // property names. Using the array syntax, its also possible to define value mappings as
  // the second array element.
  //
  // mapping: {
  //  apiPosition: 'position',
  //  apiType: [ 'type', { apiImage: 'image', apiText: 'text' }]
  // }
  parse: function(attrs) {
    // Take props from data object if it exists
    // Reminder: If there is a model prop named "data" this will not work correctly
    if (attrs.data) {
      attrs = attrs.data
    }

    _.each(this.mapping || {}, (client, api) => {
      client = [].concat(client)
      attrs[client[0]] = (client[1] ? client[1][attrs[api]] : 0) || attrs[api]
      api !== client[0] && delete attrs[api]
    })

    return attrs
  },

  serialize: function() {
    const attrs = AmpersandModel.prototype.serialize.apply(this, arguments)

    _.each(this.mapping || {}, (client, api) => {
      client = [].concat(client)
      attrs[api] = (client[1] ? _.invert(client[1])[attrs[client[0]]] : 0) || attrs[client[0]]
      api !== client[0] && delete attrs[client[0]]
    })

    return attrs
  },
})
